import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, Popconfirm, FormInstance } from "antd";
import TextArea from "antd/es/input/TextArea";

import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadImage from "../../components/UploadImage";

interface HighlightsItem {
  title: string;
  description: string;
}

interface AboutUsProps {
  heading?: string;
  subtext?: string;
  image?: string;
  highlights?: HighlightsItem[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: "About Us") => void;
  form: FormInstance;
  aboutUsData?: AboutUsProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const AboutUs: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  aboutUsData,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  useEffect(() => {
    if (!aboutUsData) return;

    if (mode === "list") {
      form.setFieldsValue({
        aboutus: {
          heading: aboutUsData.heading ?? "",
          subtext: aboutUsData.subtext ?? "",
          image: aboutUsData.image ? [aboutUsData.image] : [],
          highlights: aboutUsData.highlights ?? [],
        },
      });
    }

    if (mode === "add") {
      form.setFieldsValue({
        aboutus: {
          highlights: [{ title: "", description: "" }],
        },
      });
    }

    if (mode === "edit" && editingIndex !== null) {
      const item = aboutUsData.highlights?.[editingIndex];
      if (item) {
        form.setFieldsValue({
          aboutus: {
            highlights: [item],
          },
        });
      }
    }
  }, [mode, editingIndex, aboutUsData, form]);

  const highlights = aboutUsData?.highlights ?? [];

  return (
    <div>
      {isEditing && mode === "list" && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Heading"
              name={["aboutus", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input placeholder="Enter heading..." />
            </Form.Item>

            <Form.Item
              label="Subtext"
              name={["aboutus", "subtext"]}
              rules={[{ required: true, message: "Enter subtext" }]}
            >
              <TextArea rows={2} placeholder="Enter subtext..." />
            </Form.Item>

            <Form.Item
              label="Image"
              name={["aboutus", "image"]}
              rules={[{ required: true, message: "Upload image" }]}
            >
              <UploadImage listType="picture-card" />
            </Form.Item>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                setIsEditing(false);
                handleCancel();
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden">
          <div className="hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Image</th>
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="bg-[#F9F9F9]">
                {aboutUsData?.heading ? (
                  <tr className="border-b">
                    <td className="px-4 py-3">
                      {aboutUsData.image && (
                        <Image src={aboutUsData.image} width={80} height={60} />
                      )}
                    </td>
                    <td className="px-4 py-3">{aboutUsData.heading}</td>
                    <td className="px-4 py-3">{aboutUsData.subtext}</td>
                    <td className="px-4 py-3 text-right">
                      <EditOutlined
                        className="text-primary text-xl cursor-pointer"
                        onClick={() => setIsEditing(true)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}

                <tr>
                  <td colSpan={4} className="px-4 py-3 text-right">
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setMode("add");
                        setEditingIndex(null);
                      }}
                    >
                      Add Highlights
                    </Button>
                  </td>
                </tr>

                <tr>
                  <td colSpan={4} className="p-4">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Title</th>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {highlights.length ? (
                          highlights.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2">{item.title}</td>
                              <td className="px-4 py-2">{item.description}</td>
                              <td className="px-4 py-2 text-right">
                                <div className="flex justify-end gap-4">
                                  <EditOutlined
                                    className="cursor-pointer text-primary"
                                    onClick={() => {
                                      setMode("edit");
                                      setEditingIndex(index);
                                    }}
                                  />
                                  <Popconfirm
                                    title="Delete this highlight?"
                                    onConfirm={() =>
                                      onDelete(index, "About Us")
                                    }
                                  >
                                    <DeleteOutlined className="cursor-pointer text-red-500" />
                                  </Popconfirm>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={3}
                              className="text-center py-6 text-gray-500"
                            >
                              No highlights added
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-4">
            {aboutUsData?.heading ? (
              <div className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm">
                {aboutUsData.image && (
                  <Image
                    src={aboutUsData.image}
                    width={120}
                    height={80}
                    className="mb-2 rounded"
                    preview={false}
                  />
                )}
                <div>
                  <span className="font-semibold text-primary">Heading: </span>
                  <p className="">{aboutUsData.heading}</p>
                </div>
                <div>
                  <span className="font-semibold text-primary">Subtext: </span>
                  <p className="">{aboutUsData.subtext}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block my-1">
                    Action:
                  </span>
                  <EditOutlined
                    onClick={() => setIsEditing(true)}
                    className="cursor-pointer text-black text-[18px]"
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}

            {highlights.length ? (
              highlights.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm"
                >
                  <p>
                    <span className="font-semibold text-primary">Title:</span>{" "}
                    {item.title}
                  </p>
                  <p>
                    <span className="font-semibold text-primary">
                      Description:
                    </span>{" "}
                    {item.description}
                  </p>
                  <div className="flex justify-end gap-3 mt-2">
                    <EditOutlined
                      className="cursor-pointer text-primary"
                      onClick={() => {
                        setMode("edit");
                        setEditingIndex(index);
                      }}
                    />
                    <Popconfirm
                      title="Delete this highlight?"
                      onConfirm={() => onDelete(index, "About Us")}
                    >
                      <DeleteOutlined className="cursor-pointer text-red-500" />
                    </Popconfirm>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-6">
                No highlights added
              </div>
            )}

            <div className="text-right">
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => {
                  setMode("add");
                  setEditingIndex(null);
                }}
              >
                Add Highlights
              </Button>
            </div>
          </div>
        </div>
      )}

      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["aboutus", "highlights"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <Form.Item
                        label="Title"
                        name={[field.name, "title"]}
                        rules={[{ required: true, message: "Enter title" }]}
                      >
                        <Input placeholder="Enter title..." />
                      </Form.Item>

                      <Form.Item
                        label="Description"
                        name={[field.name, "description"]}
                        rules={[
                          { required: true, message: "Enter description" },
                        ]}
                      >
                        <TextArea rows={3} placeholder="Enter description..." />
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUs;